import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "form", "nameInput", "startTimeInput", "endTimeInput", "calendarSelect", "colorInput", "allDayInput", "dayCell", "mobileMenu", "mobileEventList", "dayEventsPopover", "dayEventsPanel", "dayEventsTitle", "dayEventsList", "eventDetail", "eventDetailPanel", "eventDetailName", "eventDetailColor", "eventDetailTime", "eventDetailCalendarRow", "eventDetailCalendar", "eventDetailRecurrenceRow", "eventDetailRecurrence", "eventDetailClientRow", "eventDetailClient", "eventDetailDesc", "eventDetailShowLink", "eventDetailEditLink"]
  static values = {
    createUrl: String,
    eventsData: Object,
    selectedDate: String,
    defaultColor: { type: String, default: "#3b82f6" },
    defaultCalendarId: { type: String, default: "" }
  }

  connect() {
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this._closeMobileMenu = this._closeMobileMenu.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this._closeMobileMenu)
  }

  // przełącz widoczność mobilnego menu (3 kropki)
  toggleMobileMenu(event) {
    event.stopPropagation()
    const menu = this.mobileMenuTarget
    const isHidden = menu.classList.contains("hidden")

    if (isHidden) {
      menu.classList.remove("hidden")
      document.addEventListener("click", this._closeMobileMenu)
    } else {
      menu.classList.add("hidden")
      document.removeEventListener("click", this._closeMobileMenu)
    }
  }

  _closeMobileMenu(event) {
    if (!this.mobileMenuTarget.contains(event.target)) {
      this.mobileMenuTarget.classList.add("hidden")
      document.removeEventListener("click", this._closeMobileMenu)
    }
  }

  // zaznacz dzień na widoku mobilnym
  selectDay(event) {
    const button = event.currentTarget
    const date = button.dataset.date

    // usuń data-is-selected z poprzednio zaznaczonego
    this.element.querySelectorAll("button[data-is-selected]").forEach(btn => {
      btn.removeAttribute("data-is-selected")
    })

    // zaznacz kliknięty dzień
    button.setAttribute("data-is-selected", "")
    this.selectedDateValue = date

    // zaktualizuj listę eventów na mobilce
    this._updateMobileEventList(date)
  }

  _updateMobileEventList(date) {
    const dayData = this.eventsDataValue[date] || {}
    const events = dayData.events || []
    const container = this.mobileEventListTarget

    if (events.length === 0) {
      container.innerHTML = ""
      return
    }

    const items = events.map(ev => `
      <li class="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50">
        <div class="flex-auto">
          <p class="font-semibold text-gray-900">${this._escapeHtml(ev.name)}</p>
          <time datetime="${this._escapeHtml(ev.datetime)}" class="mt-2 flex items-center text-gray-700">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="mr-2 size-5 text-gray-400">
              <path d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clip-rule="evenodd" fill-rule="evenodd" />
            </svg>
            ${this._escapeHtml(ev.time)}
          </time>
        </div>
        <a href="${this._escapeHtml(ev.edit_url)}" class="ml-6 flex-none self-center rounded-md bg-white px-3 py-2 font-semibold text-gray-900 opacity-0 shadow-xs ring-1 ring-gray-300 ring-inset group-hover:opacity-100 hover:ring-gray-400 focus:opacity-100">Edit</a>
      </li>
    `).join("")

    container.innerHTML = `
      <ol class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow-sm outline-1 outline-black/5">
        ${items}
      </ol>
    `
  }

  _escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    if (str == null) return ""
    return str.replace(/&/g, "&amp;").replace(/'/g, "&#39;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // otwórz modal z formularzem tworzenia eventu po kliknięciu na dzień (widok miesięczny)
  openNewEvent(event) {
    const date = event.currentTarget.dataset.date
    this._openModal(date, "09:00", "10:00")
  }

  // otwórz modal po kliknięciu na siatkę godzinową (widok tygodniowy)
  openWeekEvent(event) {
    const date = event.currentTarget.dataset.date
    const col = event.currentTarget
    const rect = col.getBoundingClientRect()
    const clickY = event.clientY - rect.top
    const totalHeight = rect.height
    // oblicz godzinę na podstawie pozycji kliknięcia (24h)
    const totalMinutes = Math.round((clickY / totalHeight) * 24 * 60)
    const hour = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes / 30) * 30 % 60
    const startHour = Math.min(Math.max(hour, 0), 23)
    const endHour = startHour + 1 > 23 ? 23 : startHour + 1
    const pad = (n) => String(n).padStart(2, "0")
    this._openModal(date, `${pad(startHour)}:${pad(minutes)}`, `${pad(endHour)}:${pad(minutes)}`)
  }

  _openModal(date, startTime, endTime) {
    this.nameInputTarget.value = ""
    this.startTimeInputTarget.value = `${date}T${startTime}`
    if (this.hasEndTimeInputTarget) {
      this.endTimeInputTarget.value = `${date}T${endTime}`
    }
    if (this.hasCalendarSelectTarget) {
      // domyślnie ustaw bieżący kalendarz (z widoku pojedynczego); jeśli brak — pusto
      this.calendarSelectTarget.value = this.defaultCalendarIdValue || ""
    }
    if (this.hasColorInputTarget) {
      this.colorInputTarget.value = this.defaultColorValue
    }
    if (this.hasAllDayInputTarget) {
      this.allDayInputTarget.checked = false
    }
    this._colorChanged = false
    this._autoEndTime = true
    this.modalTarget.classList.remove("hidden")
    document.addEventListener("keydown", this.closeOnEscape)
    this.nameInputTarget.focus()
  }

  // automatycznie ustaw end_time na godzinę po start_time
  syncEndTime() {
    if (!this.hasEndTimeInputTarget) return
    const startValue = this.startTimeInputTarget.value
    if (!startValue) return

    const endValue = this.endTimeInputTarget.value
    if (endValue && !this._autoEndTime) return

    const startDate = new Date(startValue)
    startDate.setHours(startDate.getHours() + 1)
    const pad = (n) => String(n).padStart(2, "0")
    this.endTimeInputTarget.value = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}T${pad(startDate.getHours())}:${pad(startDate.getMinutes())}`
    this._autoEndTime = true
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.closeModal()
    }
  }

  // kliknięcie na tło modala zamyka go
  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }

  // pokaż popover z pełną listą wydarzeń dnia
  showDayEvents(event) {
    event.stopPropagation()
    const date = event.currentTarget.dataset.date
    const dayData = this.eventsDataValue[date] || {}
    const events = dayData.events || []

    // tytuł z datą (sformatowany po stronie serwera)
    this.dayEventsTitleTarget.textContent = dayData.title || date

    // lista wydarzeń - klikalne buttony otwierające podgląd (showEventDetail)
    const items = events.map(ev => {
      const pos = ev.position
      const isBar = pos === "start" || pos === "end" || pos === "middle" || pos === "single"
      const evData = JSON.stringify({
        name: ev.name,
        time: ev.detail_time || ev.time,
        color: ev.color,
        calendar_name: ev.calendar_name,
        description: ev.description,
        recurrence: ev.recurrence,
        show_url: ev.url,
        edit_url: ev.edit_url
      })

      if (isBar) {
        return `<li>
          <button type="button" data-event='${this._escapeAttr(evData)}' data-action="click->calendar-dashboard#showEventDetail" class="block truncate rounded-md px-2 py-0.5 text-xs font-medium text-white hover:opacity-80 w-full text-left" style="background-color: ${this._escapeHtml(ev.color)};">
            ${this._escapeHtml(ev.name)}
          </button>
        </li>`
      }

      return `<li>
        <button type="button" data-event='${this._escapeAttr(evData)}' data-action="click->calendar-dashboard#showEventDetail" class="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 w-full text-left">
          <span class="size-2 shrink-0 rounded-full" style="background-color: ${this._escapeHtml(ev.color)};"></span>
          <span class="text-xs text-gray-500">${this._escapeHtml(ev.time)}</span>
          <span class="flex-auto truncate text-sm font-medium text-gray-900 group-hover:text-blue-600">${this._escapeHtml(ev.name)}</span>
        </button>
      </li>`
    }).join("")
    this.dayEventsListTarget.innerHTML = items

    // pozycjonowanie panelu obok klikniętego przycisku
    const btn = event.currentTarget
    const rect = btn.getBoundingClientRect()
    const panel = this.dayEventsPanelTarget
    this.dayEventsPopoverTarget.classList.remove("hidden")

    // pozycja: pod przyciskiem, wyśrodkowany w poziomie
    const panelWidth = 288
    let left = rect.left + rect.width / 2 - panelWidth / 2
    let top = rect.bottom + 4

    // nie wychodź poza ekran
    if (left < 8) left = 8
    if (left + panelWidth > window.innerWidth - 8) left = window.innerWidth - panelWidth - 8
    if (top + panel.offsetHeight > window.innerHeight - 8) top = rect.top - panel.offsetHeight - 4

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  closeDayEvents() {
    this.dayEventsPopoverTarget.classList.add("hidden")
  }

  // pokaż popover z detalami pojedynczego wydarzenia
  showEventDetail(event) {
    event.stopPropagation()
    if (!this.hasEventDetailTarget) return

    const data = JSON.parse(event.currentTarget.dataset.event)

    this.eventDetailNameTarget.textContent = data.name
    this.eventDetailColorTarget.style.backgroundColor = data.color
    this.eventDetailTimeTarget.textContent = data.time
    this.eventDetailShowLinkTarget.href = data.show_url
    this.eventDetailEditLinkTarget.href = data.edit_url

    if (data.calendar_name) {
      this.eventDetailCalendarTarget.textContent = data.calendar_name
      this.eventDetailCalendarRowTarget.classList.remove("hidden")
    } else {
      this.eventDetailCalendarRowTarget.classList.add("hidden")
    }

    if (data.recurrence && this.hasEventDetailRecurrenceRowTarget) {
      this.eventDetailRecurrenceTarget.textContent = data.recurrence
      this.eventDetailRecurrenceRowTarget.classList.remove("hidden")
    } else if (this.hasEventDetailRecurrenceRowTarget) {
      this.eventDetailRecurrenceRowTarget.classList.add("hidden")
    }

    if (data.client_name && this.hasEventDetailClientRowTarget) {
      this.eventDetailClientTarget.textContent = data.client_name
      if (data.client_url) this.eventDetailClientTarget.href = data.client_url
      this.eventDetailClientRowTarget.classList.remove("hidden")
    } else if (this.hasEventDetailClientRowTarget) {
      this.eventDetailClientRowTarget.classList.add("hidden")
    }

    if (data.description) {
      this.eventDetailDescTarget.textContent = data.description
      this.eventDetailDescTarget.classList.remove("hidden")
    } else {
      this.eventDetailDescTarget.classList.add("hidden")
    }

    this.eventDetailTarget.classList.remove("hidden")

    const rect = event.currentTarget.getBoundingClientRect()
    const panel = this.eventDetailPanelTarget
    const panelWidth = 288
    let left = rect.right + 8
    let top = rect.top

    if (left + panelWidth > window.innerWidth - 8) {
      left = rect.left - panelWidth - 8
    }
    if (left < 8) left = 8

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`

    requestAnimationFrame(() => {
      if (top + panel.offsetHeight > window.innerHeight - 8) {
        top = window.innerHeight - panel.offsetHeight - 8
      }
      if (top < 8) top = 8
      panel.style.top = `${top}px`
    })
  }

  closeEventDetail() {
    if (this.hasEventDetailTarget) {
      this.eventDetailTarget.classList.add("hidden")
    }
  }

  // zaznacz że użytkownik zmienił kolor
  markColorChanged() {
    this._colorChanged = true
  }

  // zapobiega propagacji kliknięcia (np. kliknięcie na link eventu nie otwiera modala)
  stopPropagation(event) {
    event.stopPropagation()
  }

  submitEvent(event) {
    event.preventDefault()

    const name = this.nameInputTarget.value.trim()
    if (!name) {
      this.nameInputTarget.focus()
      return
    }

    const csrfToken = document.querySelector("[name=\"csrf-token\"]").content
    const allDay = this.hasAllDayInputTarget && this.allDayInputTarget.checked
    const body = {
      event: {
        name,
        start_time: this.startTimeInputTarget.value,
        all_day: allDay
      }
    }

    if (this.hasEndTimeInputTarget && this.endTimeInputTarget.value) {
      body.event.end_time = this.endTimeInputTarget.value
    }

    if (this.hasCalendarSelectTarget && this.calendarSelectTarget.value) {
      body.event.calendar_id = this.calendarSelectTarget.value
    }

    if (this.hasColorInputTarget && this._colorChanged && this.colorInputTarget.value) {
      body.event.color = this.colorInputTarget.value
    }

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json"
      },
      body: JSON.stringify(body),
    })
      .then(response => {
        if (response.ok) {
          this.closeModal()
          window.location.reload()
        } else {
          return response.json().then(data => {
            console.error("Error:", data)
            alert("Nie udało się utworzyć wydarzenia")
          })
        }
      })
      .catch(error => {
        console.error("Error:", error)
      })
  }
}
